import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {
  LabelPickerBase,
  type CreateLabelMutationCallback,
  type LabelPickerBaseProps,
  type LabelPickerLabel as LabelPickerBaseLabel,
  type LabelSuggestion,
} from '@github-ui/item-picker-label'
import {
  keepPreviousData,
  queryOptions,
  QueryClient,
  useQuery,
  useQueryClient,
  type QueryPersister,
} from '@github-ui/react-query'
import {useAnalytics, type SendAnalyticsEventFunction} from '@github-ui/use-analytics'
import {useCallback, useMemo, useRef, useState} from 'react'
import {
  apiGetSearchLabels,
  buildItemPickerLabelSearchQueryKey,
  transformSearchLabelResponseToQueryData,
  type LabelPickerQueryData,
} from './api/api-get-search-labels'
import {
  buildItemPickerLabelSuggestionsQueryKey,
  fetchLabelSuggestions,
  type LabelSuggestionsQueryData,
} from './api/api-get-label-suggestions'
import {parseGraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {createLabelPersister} from './api/label-persister'
import {useCreateLabelMutation} from './mutations/use-create-label-mutation'

//
// LabelPickerBase <== ItemPickerFetcher (internal) <== LabelPicker (exported)
//                                                  <== BulkIssueLabelPicker (exported)

type LabelQueryKey = ReturnType<typeof buildItemPickerLabelSearchQueryKey>

// The full label list the picker opens with. Fetched once per repo and shared by
// every picker instance on the page.
const INITIAL_LABELS_COUNT = 1000

// Suggestions encode how recently and how often the viewer used each label, so
// they change only when the viewer applies a label. A minute of staleness bounds
// the network to at most one request per repo per window no matter how often the
// picker is opened, while still reflecting a triage session within the same
// sitting.
const SUGGESTIONS_STALE_TIME_MS = 60 * 1000

const NO_SUGGESTIONS: LabelSuggestion[] = []
const NO_LABELS: LabelPickerBaseLabel[] = []

export async function fetchLabels(
  queryClient: QueryClient,
  {
    owner,
    repo,
    query,
    count,
    labelNames,
    suggested,
    includeArchived,
    persister,
  }: {
    owner: string
    repo: string
    query: string
    count?: number
    labelNames?: string[]
    suggested?: boolean
    includeArchived?: boolean
    persister?: QueryPersister<LabelPickerQueryData, LabelQueryKey>
  },
): Promise<LabelPickerQueryData> {
  const response = await queryClient.fetchQuery({
    queryKey: buildItemPickerLabelSearchQueryKey({owner, repo, query, count, labelNames, suggested, includeArchived}),
    queryFn: async () => {
      const responseJson = await apiGetSearchLabels({owner, repo, query, count, labelNames, suggested, includeArchived})
      const responseData = parseGraphQLResponse(responseJson)
      return transformSearchLabelResponseToQueryData(responseData.data, labelNames)
    },
    persister,
  })
  return response
}

function buildLabelsQueryOptions({
  owner,
  repo,
  labelNames,
  includeArchived,
  persister,
}: {
  owner: string
  repo: string
  labelNames?: string[]
  includeArchived?: boolean
  persister: QueryPersister<LabelPickerQueryData, LabelQueryKey>
}) {
  return queryOptions({
    queryKey: buildItemPickerLabelSearchQueryKey({
      owner,
      repo,
      query: '',
      count: INITIAL_LABELS_COUNT,
      labelNames,
      includeArchived,
    }),
    queryFn: async () => {
      const responseJson = await apiGetSearchLabels({
        owner,
        repo,
        query: '',
        count: INITIAL_LABELS_COUNT,
        labelNames,
        includeArchived,
      })
      const responseData = parseGraphQLResponse(responseJson)
      return transformSearchLabelResponseToQueryData(responseData.data, labelNames)
    },
    persister,
  })
}

/**
 * Shared by the picker's own subscription and by the prefetch-on-intent call on
 * the anchor, so both resolve to the same cache entry and the same staleness
 * window: a prefetch that lands before the first open makes it paint
 * synchronously, and a duplicate prefetch is a no-op.
 *
 * Keyed on the repository alone, so a selection toggle never spawns a new cache
 * entry.
 */
function buildSuggestionsQueryOptions({
  owner,
  repo,
  includeArchived,
}: {
  owner: string
  repo: string
  includeArchived?: boolean
}) {
  return queryOptions({
    queryKey: buildItemPickerLabelSuggestionsQueryKey({owner, repo, includeArchived}),
    queryFn: () => fetchLabelSuggestions({owner, repo, includeArchived}),
    staleTime: SUGGESTIONS_STALE_TIME_MS,
  })
}

function useLabelPersister(sendAnalyticsEvent: SendAnalyticsEventFunction) {
  return useMemo<QueryPersister<LabelPickerQueryData, LabelQueryKey>>(
    () => (queryFn, context, query) => createLabelPersister(sendAnalyticsEvent)(queryFn, context, query),
    [sendAnalyticsEvent],
  )
}

export type LabelPickerLabel = LabelPickerBaseLabel

export type LabelPickerProps = Omit<LabelPickerBaseProps, 'filterItems' | 'suggestedLabels'> & {
  labelNames?: string[]
  /**
   * When true, this surface opts into the server-ranked "Suggestions" group at
   * the top of the picker.
   *
   * CONTRACT-GAP(suggested_labels): this is currently the *only* gate, so it
   * also decides the taller picker and the regrouped list. It expresses "this
   * surface wants suggestions", not "the experience is enabled for this viewer",
   * which the client cannot determine on its own: the server evaluates
   * `suggested_labels` against the viewer, the repository *and* the repository
   * owner, so no client-preloaded feature flag can reproduce it. See
   * `suggestionsEnabled` in `ItemPickerFetcher` for the single place to switch
   * this onto a server-sent signal once the payload carries one.
   */
  enableSuggestedLabels?: boolean
  includeArchived?: boolean
}

/**
 * Prefetch on intent. `prefetchQuery` respects the query's `staleTime`, so
 * hovering a warm anchor costs nothing, while hovering after a selection
 * invalidated the ranking starts the refresh before the panel opens.
 */
function useSuggestionsPrefetch({
  owner,
  repo,
  includeArchived,
  enabled,
}: {
  owner: string
  repo: string
  includeArchived?: boolean
  enabled?: boolean
}) {
  const queryClient = useQueryClient()

  return useCallback(() => {
    if (!enabled) return
    queryClient.prefetchQuery(buildSuggestionsQueryOptions({owner, repo, includeArchived}))
  }, [enabled, includeArchived, owner, queryClient, repo])
}

function withSuggestionsPrefetch(
  anchorElement: LabelPickerProps['anchorElement'],
  prefetchSuggestions: () => void,
): LabelPickerProps['anchorElement'] {
  return (anchorProps, ref) =>
    anchorElement(
      {
        ...anchorProps,
        onMouseEnter: event => {
          anchorProps.onMouseEnter?.(event)
          prefetchSuggestions()
        },
        onFocus: event => {
          anchorProps.onFocus?.(event)
          prefetchSuggestions()
        },
      },
      ref,
    )
}

function ItemPickerFetcher({
  repo,
  owner,
  labels,
  labelNames,
  noLabelOption,
  enableSuggestedLabels,
  includeArchived,
  onSelectionChange,
  ...rest
}: LabelPickerProps) {
  const queryClient = useQueryClient()
  const {mutate: createLabelMutate} = useCreateLabelMutation()
  const {sendAnalyticsEvent} = useAnalytics()

  // Tracks the create-label mutation only; initial data loading is owned by the
  // queries below.
  const [isMutating, setIsMutating] = useState(false)

  const persister = useLabelPersister(sendAnalyticsEvent)

  const labelsQuery = useQuery(buildLabelsQueryOptions({owner, repo, includeArchived, persister}))

  // Labels supplied by name only (e.g. the issues list label filter reads its
  // selected labels from the URL query) have to be resolved to full labels.
  //
  // `keepPreviousData` is what makes re-resolution non-blocking: a selection
  // toggle rewrites the URL, which flows back in as new `labelNames` and so a
  // new query key. The picker already holds a valid selection, so it keeps
  // rendering the previous resolution while the new one is in flight instead of
  // flashing back into a loading state.
  const needsSelectedLabels = Boolean(labelNames && labelNames.length > 0)
  const selectedLabelsQuery = useQuery({
    ...buildLabelsQueryOptions({owner, repo, labelNames, includeArchived, persister}),
    enabled: needsSelectedLabels,
    placeholderData: keepPreviousData,
  })

  // CONTRACT-GAP(suggested_labels): single resolution point for "is the
  // suggestions experience on for this viewer". Swap the right-hand side for the
  // server-sent signal once the suggestions payload carries one; nothing else in
  // this file needs to change.
  const suggestionsEnabled = Boolean(enableSuggestedLabels)

  // Never refetch while the panel is open: the rendered group is snapshotted per
  // open anyway, so an in-flight refresh could only cost network, never change
  // what the user is looking at. Initialized to `true` because this component is
  // only mounted once the picker is already being opened.
  const isPanelOpenRef = useRef(true)

  const suggestionsQuery = useQuery({
    ...buildSuggestionsQueryOptions({owner, repo, includeArchived}),
    enabled: suggestionsEnabled,
    // Cached-first: `staleTime` means a warm cache paints immediately and
    // coalesces every open within the window into zero requests, while TanStack
    // dedupes concurrent ones. Revalidation happens on window focus, which only
    // fires on the active tab, so background tabs never refetch.
    refetchOnWindowFocus: () => !isPanelOpenRef.current,
    refetchOnReconnect: false,
    // Suggestions are non-essential decoration: a failed request should leave the
    // picker fully usable rather than retry behind the user's back.
    retry: false,
    // Deliberately unpersisted: replaying a day-old snapshot of per-viewer
    // recency would mask the very signal this group exists to surface. See
    // `createLabelPersister`.
  })

  // `ItemPicker` memoizes each item's props by key the first time it renders
  // them and only refreshes `disabled` afterwards, so a label first rendered
  // before its suggestion arrived keeps that grouping and loses its reason
  // subtext for the life of the panel. The label list is commonly served from
  // the persisted cache while suggestions still need a round trip, so the list
  // is held back until suggestions have settled rather than raced against them.
  // Prefetch-on-intent plus `staleTime` make this free on a warm cache, and a
  // failed suggestions request settles immediately (`retry: false`).
  const isSuggestionsPending = suggestionsEnabled && suggestionsQuery.isPending

  // Snapshot-on-open: the rendered suggestions group is bound to a snapshot
  // taken when the panel opens, never to live query data. A background
  // revalidation lands in the cache but must not reorder or swap items under the
  // user's cursor; the new set is picked up on the next open.
  //
  // `null` means "nothing captured for this open yet", which is also the cold
  // cache case: the first payload to arrive is captured, giving a single
  // loading-to-content reveal rather than a swap.
  const [openSuggestions, setOpenSuggestions] = useState<LabelSuggestion[] | null>(
    () =>
      queryClient.getQueryData<LabelSuggestionsQueryData>(
        buildItemPickerLabelSuggestionsQueryKey({owner, repo, includeArchived}),
      )?.suggestions ?? null,
  )
  const suggestionsData = suggestionsQuery.data
  if (openSuggestions === null && suggestionsData) {
    // Capturing during render rather than in an effect keeps a cold open from
    // painting an intermediate frame without the group.
    setOpenSuggestions(suggestionsData.suggestions)
  }

  // `LazyItemPicker` mounts this component once and keeps it mounted, so an open
  // is not a remount: the snapshot has to hang off the panel's own open
  // transition.
  const handlePickerOpen = useCallback(() => {
    isPanelOpenRef.current = true
    setOpenSuggestions(suggestionsData?.suggestions ?? null)
    // Because this component never remounts and `invalidateSuggestions` marks the
    // ranking stale without refetching, opening is the only deterministic point
    // left to pick that staleness up. Scoped to stale entries so an ordinary
    // reopen still costs nothing. The result lands in the cache behind the
    // snapshot taken above, so it reorders nothing under the user's cursor.
    if (suggestionsEnabled) {
      queryClient.refetchQueries({
        queryKey: buildItemPickerLabelSuggestionsQueryKey({owner, repo, includeArchived}),
        stale: true,
      })
    }
  }, [includeArchived, owner, queryClient, repo, suggestionsData, suggestionsEnabled])

  const handlePickerClose = useCallback(() => {
    isPanelOpenRef.current = false
  }, [])

  // Applying a label is exactly what changes the viewer's recency ranking, so
  // mark the suggestions stale afterwards. `refetchType: 'none'` deliberately
  // does not refetch: the panel is closing, the fresh ranking is only needed on
  // the next open, and rapid triage would otherwise fire a request per applied
  // label.
  const invalidateSuggestions = useCallback(() => {
    if (!suggestionsEnabled) return
    queryClient.invalidateQueries({
      queryKey: buildItemPickerLabelSuggestionsQueryKey({owner, repo, includeArchived}),
      refetchType: 'none',
    })
  }, [includeArchived, owner, queryClient, repo, suggestionsEnabled])

  const handleSelectionChange = useCallback(
    (selectedLabels: LabelPickerLabel[]) => {
      invalidateSuggestions()
      onSelectionChange(selectedLabels)
    },
    [invalidateSuggestions, onSelectionChange],
  )

  const anchorRef = useRef<HTMLButtonElement>(null)

  // `LazyItemPicker` hands this component the caller's original anchor, so the
  // prefetch wrapper it applied before the first open is gone from here on. Rewrap
  // it: every open after the first is served by this anchor, and those are exactly
  // the opens whose ranking may have been invalidated by an earlier selection.
  const prefetchSuggestions = useSuggestionsPrefetch({owner, repo, includeArchived, enabled: suggestionsEnabled})
  const anchorElement = useMemo(
    () => withSuggestionsPrefetch(rest.anchorElement, prefetchSuggestions),
    [prefetchSuggestions, rest.anchorElement],
  )

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('labels'),
    anchorElement: anchorProps => rest.anchorElement(anchorProps, anchorRef),
    open: true,
  })

  const fetchLabelsWithQueryClient = useCallback(
    async (query: string) => {
      const response = await fetchLabels(queryClient, {owner, repo, query, includeArchived})
      return response.labels
    },
    [includeArchived, queryClient, owner, repo],
  )

  const onCreate: CreateLabelMutationCallback = useCallback(
    (input, {onSuccess, onError}) => {
      createLabelMutate(input, {
        onSuccess: response => {
          invalidateSuggestions()
          onSuccess(response)
        },
        onError,
      })
    },
    [createLabelMutate, invalidateSuggestions],
  )

  // Handle noLabelOption for suggestions (add to the beginning of the list)
  const suggestions = useMemo(() => {
    if (isSuggestionsPending) return NO_LABELS
    const suggestionsList = labelsQuery.data?.labels ?? []
    if (noLabelOption) {
      return [SPECIAL_VALUES.noLabelsData as LabelPickerLabel, ...suggestionsList]
    }
    return suggestionsList
  }, [isSuggestionsPending, labelsQuery.data?.labels, noLabelOption])

  // Handle noLabelOption for selected labels (add when noLabelOption.selected is true)
  const allLabels = useMemo(() => {
    const labelsList = needsSelectedLabels ? (selectedLabelsQuery.data?.labelsByNames ?? []) : labels
    if (noLabelOption?.selected && !labelsList.find(l => l.id === SPECIAL_VALUES.noLabelsData.id)) {
      return [SPECIAL_VALUES.noLabelsData as LabelPickerLabel, ...labelsList]
    }
    return labelsList
  }, [needsSelectedLabels, labels, noLabelOption?.selected, selectedLabelsQuery.data?.labelsByNames])

  // The picker must not render until the label list *and* any preselected labels
  // have resolved. Committing an empty selection while the URL still carries
  // label filters would fire `onSelectionChange` without them and strip the
  // filters that are still on screen.
  const isLoading =
    labelsQuery.isPending || (needsSelectedLabels && selectedLabelsQuery.isPending) || isSuggestionsPending
  const isError = labelsQuery.isError || (needsSelectedLabels && selectedLabelsQuery.isError)

  if (isError) {
    return createFallbackComponent(() => {
      labelsQuery.refetch()
      if (needsSelectedLabels) selectedLabelsQuery.refetch()
    })
  }

  return (
    <LabelPickerBase
      repo={repo}
      owner={owner}
      repositoryId={labelsQuery.data?.repositoryId ?? ''}
      labels={allLabels}
      suggestions={suggestions}
      suggestedLabels={openSuggestions ?? NO_SUGGESTIONS}
      enableSuggestedLabels={suggestionsEnabled}
      totalNumberOfLabels={labelsQuery.data?.totalCount ?? 0}
      isLoading={isLoading || isMutating}
      setIsLoading={setIsMutating}
      searchLabels={fetchLabelsWithQueryClient}
      onCreate={onCreate}
      onSelectionChange={handleSelectionChange}
      onPickerOpen={handlePickerOpen}
      onPickerClose={handlePickerClose}
      noLabelOption={noLabelOption}
      {...rest}
      anchorElement={anchorElement}
    />
  )
}

export function LabelPicker({
  repo,
  owner,
  anchorElement,
  readonly,
  labels,
  labelNames,
  noLabelOption,
  enableSuggestedLabels,
  includeArchived,
  ...rest
}: LabelPickerProps) {
  // Prefetch on intent. The fetcher does not exist until the anchor is
  // activated, so warming the cache from hover/focus here is what lets the very
  // first open paint suggestions synchronously instead of revealing them a beat
  // later. The fetcher rewraps its own anchor for every later open.
  const prefetchSuggestions = useSuggestionsPrefetch({
    owner,
    repo,
    includeArchived,
    enabled: enableSuggestedLabels,
  })

  return (
    <LazyItemPicker
      keybindingCommandId="item-pickers:open-labels"
      anchorElement={withSuggestionsPrefetch(anchorElement, prefetchSuggestions)}
      createChild={() => (
        <ItemPickerFetcher
          repo={repo}
          owner={owner}
          readonly={readonly}
          initialFilter={''}
          labels={labels}
          labelNames={labelNames}
          enableSuggestedLabels={enableSuggestedLabels}
          includeArchived={includeArchived}
          triggerOpen
          anchorElement={anchorElement}
          noLabelOption={noLabelOption}
          {...rest}
        />
      )}
      insidePortal={rest.insidePortal}
    />
  )
}
